import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const SLUG = 'tatouaz-pleura-poso-ponaei-athina';
const URL = `https://210tattoo.gr/blog/${SLUG}`;

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Πόσο πονάει το τατουάζ στα πλευρά;", acceptedAnswer: { "@type": "Answer", text: "Το τατουάζ στα πλευρά είναι από τα πιο επώδυνα, με έντονη, διαπεραστική αίσθηση λόγω του λεπτού δέρματος πάνω από κόκαλο, της πυκνής νεύρωσης και της συνεχούς κίνησης από την αναπνοή. Ο πόνος όμως είναι διαχειρίσιμος με σωστή προετοιμασία." } },
    { "@type": "Question", name: "Είναι όλα τα σημεία στα πλευρά το ίδιο επώδυνα;", acceptedAnswer: { "@type": "Answer", text: "Όχι. Τα σημεία πιο κοντά στη σπονδυλική στήλη είναι λίγο πιο ανεκτά. Τα σημεία προς το στέρνο είναι πιο έντονα. Τα κατώτερα πλευρά είναι κάπως πιο ανεκτά από τα ανώτερα." } },
    { "@type": "Question", name: "Πόσο κρατάει ο πόνος από τατουάζ στα πλευρά;", acceptedAnswer: { "@type": "Answer", text: "Ο έντονος πόνος κρατάει όσο η συνεδρία. Μετά, θα νιώσεις μια πονεμένη, γδαρμένη αίσθηση για μερικές ημέρες, όπως ένα ηλιακό έγκαυμα, που υποχωρεί σταδιακά." } },
    { "@type": "Question", name: "Πρέπει να χωρίσω το τατουάζ σε πολλαπλές συνεδρίες;", acceptedAnswer: { "@type": "Answer", text: "Για μεγαλύτερα κομμάτια, ναι, είναι μια εξαιρετική ιδέα. Οι πολλαπλές συνεδρίες είναι πιο ήπιες, δίνουν καλύτερα αποτελέσματα και σου επιτρέπουν να ανακάμπτεις ανάμεσά τους." } },
    { "@type": "Question", name: "Πού μπορώ να κάνω τατουάζ στα πλευρά στην Αθήνα;", acceptedAnswer: { "@type": "Answer", text: "Το 210 Tattoo Athens έχει εμπειρία σε τατουάζ στα πλευρά και άλλα δύσκολα σημεία. Στείλε μήνυμα στο @210tattoo στο Instagram για να συζητήσετε το σχέδιο και την προετοιμασία σου." } },
  ],
};

export default function TatouazPleuraPosoPonaeiAthina() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Τατουάζ στα Πλευρά: Πόσο Πονάει;",
      "description": "Οδηγός για την Αθήνα: γιατί το τατουάζ στα πλευρά είναι από τα πιο επώδυνα, πόσο πονάει πραγματικά, τι να περιμένεις και πώς να προετοιμαστείς σωστά.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-07-07",
      "dateModified": "2026-07-07",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": URL,
      "inLanguage": "el"
    };
    inject('schema-ribs-faq', faqSchema);
    inject('schema-ribs-article', articleSchema);
    return () => {
      document.getElementById('schema-ribs-faq')?.remove();
      document.getElementById('schema-ribs-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Τατουάζ στα Πλευρά: Πόσο Πονάει; | 210 Tattoo Athens</title>
        <meta name="description" content="Πόσο πονάει το τατουάζ στα πλευρά; Γιατί είναι από τα πιο επώδυνα σημεία, τι να περιμένεις, πώς να προετοιμαστείς και να διαχειριστείς τη συνεδρία στην Αθήνα." />
        <link rel="canonical" href={URL} />
        <link rel="alternate" hrefLang="el" href={URL} />
        <link rel="alternate" hrefLang="x-default" href={URL} />
        <meta property="og:title" content="Τατουάζ στα Πλευρά: Πόσο Πονάει; | 210 Tattoo Athens" />
        <meta property="og:description" content="Γιατί πονούν τα πλευρά, τι να περιμένεις και πώς να προετοιμαστείς σωστά." />
        <meta property="og:url" content={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage(SLUG)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Τατουάζ στα Πλευρά: Πόσο Πονάει; | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Γιατί πονούν τα πλευρά και πώς να διαχειριστείς τη συνεδρία." />
        <meta name="twitter:image" content={ogImage(SLUG)} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Τατουάζ στα Πλευρά</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Τατουάζ στα πλευρά</span>
              <br />
              <span className="text-white">πόσο πονάει;</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">7 Ιουλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Θα είμαστε ειλικρινείς: τα πλευρά είναι σταθερά στη λίστα με τα πιο επώδυνα σημεία. Δεν είναι όμως ανυπόφορα. Δες ακριβώς γιατί πονάει, πόσο, τι να περιμένεις και πώς να προετοιμαστείς σωστά.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Why it hurts */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Γιατί τα πλευρά <span className="text-pink-500">πονούν τόσο πολύ</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Δεν είναι ένας λόγος, αλλά μια σειρά από παράγοντες που συνδυάζονται.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Λεπτό δέρμα πάνω από κόκαλο', desc: 'Το δέρμα στα πλευρά είναι λεπτό και ακριβώς από κάτω βρίσκονται τα κόκαλα, με ελάχιστη σάρκα ανάμεσα. Η δόνηση μεταφέρεται απευθείας στο κόκαλο.' },
                { title: 'Πυκνή νεύρωση', desc: 'Πυκνά αισθητικά νεύρα τρέχουν κατά μήκος των μεσοπλεύριων διαστημάτων, κάνοντας την περιοχή ευαίσθητη σε κάθε αίσθηση.' },
                { title: 'Συνεχής κίνηση από την αναπνοή', desc: 'Τα πλευρά κινούνται κάθε φορά που αναπνέεις. Η περιοχή δεν μένει σταθερή, και κάθε αναπνοή σε φέρνει σε επαφή με την ένταση.' },
                { title: 'Κοντινότητα σε ευαίσθητες δομές', desc: 'Η περιοχή βρίσκεται κοντά σε λεμφαδένες, πνεύμονες και εσωτερικά όργανα, οπότε το σώμα την προστατεύει με μεγαλύτερη ευαισθησία.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How much & where */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πόσο ακριβώς πονάει <span className="text-pink-500">και πού περισσότερο</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Οι πελάτες μας περιγράφουν μια αίσθηση σαν να τραβιέται το δέρμα πολύ έντονα, σε συνδυασμό με μια δονητική, καυτή αίσθηση που πηγαίνει βαθιά. Άλλοι το περιγράφουν σαν ένα συνεχές γρατζούνισμα με αιχμηρό εργαλείο. Ο πόνος τείνει να είναι διαπεραστικός αντί για επιφανειακός, κάτι που κάνει την εμπειρία διαφορετική από πιο ανεκτά σημεία όπως ο εξωτερικός βραχίονας.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Ο πόνος δεν είναι σταθερός σε όλη την περιοχή. Τα σημεία πιο κοντά στη σπονδυλική στήλη είναι λίγο πιο ανεκτά, ενώ προς το στέρνο ο πόνος γίνεται πιο έντονος. Τα κατώτερα πλευρά είναι κάπως πιο ανεκτά από τα ανώτερα, καθώς έχουν λίγο περισσότερη μαλακή σάρκα. Αν συγκρίνεις με άλλες περιοχές, δες τον πλήρη οδηγό μας για τα <Link to="/blog/pio-epodyna-simeia-tatouaz-athina" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">πιο επώδυνα σημεία για τατουάζ</Link>.
            </p>
          </div>
        </section>

        {/* Preparation */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς να προετοιμαστείς <span className="text-pink-500">για τατουάζ στα πλευρά</span></h2>
            <div className="space-y-4">
              {[
                { title: 'Ξεκίνα την προετοιμασία νωρίς', desc: 'Τις μέρες πριν, κοιμήσου καλά, τρώγε ισορροπημένα και πίνε πολύ νερό. Το σώμα σου χρειάζεται να είναι στην καλύτερη δυνατή κατάσταση.' },
                { title: 'Φάε ένα γεμάτο γεύμα', desc: 'Μία με δύο ώρες πριν, με σύνθετους υδατάνθρακες και πρωτεΐνη για σταθερή ενέργεια. Απόφυγε το αλκοόλ την προηγούμενη νύχτα.' },
                { title: 'Έχε μαζί σου νερό και σνακ', desc: 'Οι συνεδρίες στα πλευρά είναι κουραστικές και το σάκχαρο μπορεί να πέσει. Ένα σνακ βοηθάει στα διαλείμματα.' },
                { title: 'Ενημέρωσε τον καλλιτέχνη σου', desc: 'Αν είναι η πρώτη σου φορά σε ευαίσθητο σημείο ή έχεις χαμηλότερο κατώφλι πόνου, πες το ώστε να προσαρμόσουμε την προσέγγιση.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-base leading-relaxed mt-6">
              Δες και τι <Link to="/blog/what-to-eat-before-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">να φας πριν από το τατουάζ</Link> για σταθερή ενέργεια στη συνεδρία.
            </p>
          </div>
        </section>

        {/* Managing the session */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Διαχείριση της συνεδρίας <span className="text-pink-500">και πολλαπλές συνεδρίες</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Η αναπνοή είναι το πιο ισχυρό εργαλείο σου. Αντί να κρατάς την ανάσα όταν πονάς, ανάπνευσε βαθιά και σταθερά, εκπνέοντας αργά για να χαλαρώσει το σώμα σου. Μη διστάσεις να ζητήσεις διάλειμμα, ένας καλός καλλιτέχνης καταλαβαίνει πότε χρειάζεσαι να ανασάνεις. Μουσική ή podcast στα ακουστικά βοηθούν πολλούς να αποσπούν την προσοχή τους.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Για μεγαλύτερα κομμάτια, μια πολύ έξυπνη προσέγγιση είναι η διάσπαση της δουλειάς σε πολλαπλές συνεδρίες αντί για μία μακρά. Ο πόνος συσσωρεύεται με τον χρόνο, οπότε μια συνεδρία δύο ωρών είναι σαφώς πιο διαχειρίσιμη από μία πέντε ωρών. Το σώμα ανακάμπτει ανάμεσα, η ποιότητα είναι καλύτερη και το κόστος συνήθως δεν αλλάζει, γιατί η συνολική δουλειά είναι η ίδια.
            </p>
          </div>
        </section>

        {/* Worth it / artist */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Αξίζει τον κόπο; <span className="text-pink-500">Η ειλικρινής απάντηση</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Ναι, αξίζει. Τα τατουάζ στα πλευρά είναι εντυπωσιακά, ακολουθούν όμορφα τις γραμμές του σώματος και προσφέρουν χώρο για κομμάτια με πραγματική παρουσία. Ο πόνος είναι έντονος αλλά προσωρινός, κρατάει όσο η συνεδρία και μετά περνάει. Το τατουάζ όμως μένει για δεκαετίες.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Σε ένα δύσκολο σημείο όπως τα πλευρά, η επιλογή του καλλιτέχνη είναι ακόμα πιο σημαντική. Ένας έμπειρος tattoo artist χειρίζεται τη βελόνα με σταθερότητα, δουλεύει με τον ρυθμό της αναπνοής σου και ξέρει πότε να κάνει διάλειμμα. Η ομάδα του 210 Tattoo Athens έχει χιλιάδες ώρες εμπειρίας σε ευαίσθητα σημεία και ξέρει πώς να μετατρέψει μια δύσκολη εμπειρία σε ένα υπέροχο αποτέλεσμα.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Σκέφτεσαι τατουάζ <span className="text-pink-500">στα πλευρά;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μήνυμα στο Instagram του στούντιο, περίγραψε το σχέδιο και τη θέση που σκέφτεσαι, και η ομάδα θα σου δώσει την πλήρη εικόνα του τι να περιμένεις και πώς να προετοιμαστείς.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Μήνυμα
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/pio-epodyna-simeia-tatouaz-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Τα πιο Επώδυνα Σημεία για Τατουάζ</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Ποια σημεία πονούν περισσότερο και πώς να τα διαχειριστείς.</p>
              </Link>
              <Link to="/blog/tattoo-pain-guide-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πονάει το Τατουάζ;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πόσο πονάει πραγματικά και τι να κάνεις πριν τη συνεδρία.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
              <a href="/blackwork-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Blackwork Tattoo Athens</a>
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
